/**
 * Talking to WordPress.
 *
 * One place that knows the routes, the nonce and what a failure looks like.
 * Every call returns either the thing or an Error with a message written for a
 * customer, because these messages end up on screen.
 */

import type { Design, TrimboxDocument } from './model/types';
import type { Asset } from './assets';

/** What PHP puts on the page for us. */
export interface Template {
  uuid: string;
  title: string;
  thumb: string;
  pages: number;
  size: { w: number; h: number };
  /** Whether it was drawn for the shape this product prints. */
  fits: boolean;
}

import type { ThreeConfig } from './three/types';

export interface BootConfig {
  root: string;
  nonce: string;
  /** The design to open. Empty means start a new one. */
  uuid: string;
  product: {
    id: number;
    variation: number;
    name: string;
    /**
     * The variation's attributes, as the shop words them.
     *
     * Shown beside the product name: a 90 x 50 card and an 85 x 55 card look
     * identical on screen until something says which one is being designed.
     */
    chose?: Array<{ label: string; value: string }>;
  };
  /** Trim size and margins for this product, in mm. */
  size: { w: number; h: number; bleed: number; safe: number };
  /** A page's own size, when the product's pages differ (3D print areas); else config.size. */
  pages: Array<{
    id: string;
    name: string;
    spread?: boolean;
    size?: { w: number; h: number };
    cut?: string;
    /** That cut is a circle: a medal insert, a coaster, a round sticker. */
    round?: boolean;
    /** A canvas's bar depth, when the page wraps round it. */
    wrap?: number;
    /** How many canvases this page is printed as. */
    panels?: number;
    /** What the sides are, when the press file adds them. */
    edge?: { depth: number; mode: 'mirror' | 'colour'; colour: string };
    /** Parts of the sheet that hardware hides - a cassette, a hem, a pole pocket. */
    /** Where the eyelets are punched, worked out on the server. */
    shade?: Array<{ x: number; y: number; w: number; h: number; name?: string }>;
    eyelets?: Array<{ x: number; y: number }>;
  }>;
  /**
   * The product names its pages, and the design must keep exactly those: no
   * adding, removing, reordering or renaming. Absent or false leaves the
   * customer free to add pages.
   */
  pagesFixed?: boolean;
  /**
   * The shop's faces, grouped by family so the page stays small.
   *
   * A real library is a couple of thousand faces; one record each would be two
   * hundred kilobytes of JSON to parse before anything is drawn. Each face is
   * [ filename, weight, italic ], and the folder is said once per family.
   */
  fonts: Array<{
    family: string;
    url: string;
    faces: Array<[ string, number, number ]>;
  }>;
  returnUrl: string;
  addToCartUrl: string;
  /**
   * The design is on a basket line already: opened from "Something to
   * change?". Saving it is the change, so the button updates the basket
   * instead of adding a second copy of the same design.
   */
  inBasket?: boolean;
  /** Whose editor this is, for the header. Absent on pages rendered before it existed. */
  shop?: { name: string; logo: string; home: string };
  /**
   * Who is designing. A visitor designs in their own browser and signs in to
   * save; absent means a page from before accounts were required, which is
   * treated as signed in so nothing changes for it.
   */
  account?: {
    signedIn: boolean;
    name: string;
    /** My account, with the way back to this card on it. */
    signIn: string;
    /** Whether that page offers registration as well. */
    canRegister: boolean;
  };
  /** Set when the shop is drawing one of its templates rather than a customer a card. */
  template?: TemplateMode;
  /** The product's corner question, when it has one. See CornerQuestion. */
  corners?: CornerQuestion | null;
  /**
   * A booklet: how many printed pages it is sold with, and - when the shop
   * prices page counts through its product options - the choices on offer.
   */
  booklet?: { pages: number; question?: PagesQuestion | null } | null;
  /**
   * Where the sheet folds, from the product: a bi-fold, a tri-fold, a folded
   * card. Positions in mm, and the panels' names for the guides.
   */
  folds?: { across: number[]; down: number[]; names?: string[] } | null;
  /**
   * What the shop asks the customer to confirm before ordering, in the shop's
   * own words. Absent when this product asks nothing.
   */
  confirm?: { text: string; label: string } | null;
  /** 3D preview, present only when the shop has switched it on and completed its setup. */
  three?: ThreeConfig | null;
}

/**
 * How a product asks how many pages, from the shop's product options.
 *
 * The same shape as the corner question, because it is the same arrangement:
 * an answer that changes the design, priced where everything else is priced.
 */
export interface PagesQuestion {
  label: string;
  answers: Array<{ key: string; label: string; count: number; price: string }>;
  chosen: string;
  fallback: string;
}

/**
 * The shop's graphics library: what a customer can add from Elements.
 *
 * A drawing (`svg`) is read into real outlines when it is placed; a picture is
 * copied into the design and placed as one.
 */
export interface Graphics {
  categories: Array<{ slug: string; name: string }>;
  items: Array<{ id: number; name: string; url: string; thumb: string; kind: 'svg' | 'image'; cats: string[] }>;
}

/**
 * The shop's icon library: files on disk, searched by the server.
 *
 * Paged, unlike the drawings above, because a set worth having is a few hundred
 * and a good one is a few thousand - and a panel handed all of them would spend
 * its time drawing icons nobody asked for.
 */
export interface Icons {
  items: Array<{ id: string; name: string; category: string; words: string; url: string }>;
  categories: Array<{ name: string; count: number }>;
  total: number;
  page: number;
  pages: number;
}

/** The editor opened on one of the shop's templates. */
export interface TemplateMode {
  /** The template's post, for the way back to its screen. */
  id: number;
  title: string;
  /** Its screen in the admin. */
  editUrl: string;
}

/**
 * How a product asks about corners, from the shop's product options.
 *
 * The question lives in the options plugin; the designer only shows it and
 * draws what it means. Each answer carries its radius - 0 for square - and a
 * few words about its price, already written by the plugin that knows.
 */
export interface CornerQuestion {
  label: string;
  answers: Array<{ key: string; label: string; radius: number; price: string }>;
  /** The answer chosen on the product page before coming here, if any. */
  chosen: string;
  /** The answer to use when nothing has been chosen. */
  fallback: string;
}

declare global {
  interface Window {
    trimboxConfig?: BootConfig;
  }
}

let config: BootConfig | null = null;

export function setConfig(next: BootConfig) {
  config = next;
}

export function getConfig(): BootConfig {
  if (!config) throw new Error('Trimbox was started without its configuration.');

  return config;
}

/** Whether the product holds the design to its own pages. False with no configuration at all. */
export function pagesFixed(): boolean {
  return !!config?.pagesFixed;
}

/** Whether whoever is designing has an account to save to. */
export function signedIn(): boolean {
  return config?.account ? config.account.signedIn : true;
}

/** Whether the shop is drawing a template rather than a customer a card. */
export function templateMode(): boolean {
  return !!config?.template;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { root, nonce } = getConfig();

  const response = await fetch(`${root.replace(/\/$/, '')}/trimbox/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    // The server writes these for customers; passing them through beats
    // inventing a second, vaguer message here.
    const problem = await response.json().catch(() => null);

    throw new Error(problem?.message || 'Something went wrong talking to the shop. Please try again.');
  }

  return response.json() as Promise<T>;
}

export const api = {
  list: (product?: number) =>
    call<Design[]>('GET', product ? `/designs?product=${product}` : '/designs'),

  get: (uuid: string) => call<Design>('GET', `/designs/${uuid}`),

  create: (document: TrimboxDocument, title?: string) =>
    call<Design>('POST', '/designs', { document, title }),

  save: (uuid: string, document: TrimboxDocument, extra: { title?: string; version?: boolean | string } = {}) =>
    call<Design>('PUT', `/designs/${uuid}`, { document, ...extra }),

  rename: (uuid: string, title: string, document: TrimboxDocument) =>
    call<Design>('PUT', `/designs/${uuid}`, { document, title }),

  duplicate: (uuid: string) => call<Design>('POST', `/designs/${uuid}/duplicate`),

  remove: (uuid: string) => call<{ deleted: boolean }>('DELETE', `/designs/${uuid}`),

  versions: (uuid: string) =>
    call<Array<{ id: number; name: string; saved: string; bytes: number }>>('GET', `/designs/${uuid}/versions`),

  restore: (uuid: string, version: number) =>
    call<Design>('POST', `/designs/${uuid}/versions`, { version }),

  validate: (uuid: string) => call<{ ok: boolean; problems: string[] }>('GET', `/designs/${uuid}/validate`),

  /** Every picture on a design, with links that are good for a few hours. */
  assets: (uuid: string) => call<Asset[]>('GET', `/designs/${uuid}/assets`),

  /**
   * What this product offers to start from.
   *
   * Keyed by product on the way out as well as in: a template belongs to
   * whoever drew it, and the product listing it is the only thing that entitles
   * a customer to read it.
   */
  templates: (product: number, variation = 0) =>
    call<Template[]>('GET', `/templates?product=${product}${variation ? `&variation=${variation}` : ''}`),

  template: (product: number, uuid: string) =>
    call<{ document: TrimboxDocument; assets: Asset[] }>('GET', `/templates/${uuid}?product=${product}`),

  /**
   * One uploaded picture.
   *
   * Multipart, and deliberately not the JSON helper above: a 40 MB photo
   * base64-encoded into a JSON body is a third larger and has to be held whole
   * in memory at both ends. Content-Type is left unset so the browser can add
   * the multipart boundary, which it will not do if we set it ourselves.
   */
  upload: async (uuid: string, file: File): Promise<Asset> => {
    const { root, nonce } = getConfig();
    const body = new FormData();

    body.append('file', file);

    const response = await fetch(`${root.replace(/\/$/, '')}/trimbox/v1/designs/${uuid}/assets`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': nonce },
      body,
    });

    if (!response.ok) {
      const problem = await response.json().catch(() => null);

      throw new Error(problem?.message || 'That picture could not be uploaded. Please try again.');
    }

    return response.json() as Promise<Asset>;
  },

  removeAsset: (id: string) => call<{ deleted: boolean }>('DELETE', `/assets/${id}`),

  /** A picture of the design, for the basket line. */
  thumb: (uuid: string, dataUrl: string, kind: '' | '3d' | 'pair' = '') =>
    call<{ thumb: string }>('POST', `/designs/${uuid}/thumb`, { image: dataUrl, ...(kind ? { kind } : {}) }),

  /** The shop's own drawings, with the sections they are filed in. */
  graphics: () => call<Graphics>('GET', '/graphics'),

  icons: (query = '', category = '', page = 1) =>
    call<Icons>(
      'GET',
      `/icons?q=${encodeURIComponent(query)}&cat=${encodeURIComponent(category)}&page=${page}`
    ),

  /** One of them, copied into this design as a picture. */
  placeGraphic: (uuid: string, graphic: number) =>
    call<Asset>('POST', `/designs/${uuid}/graphics`, { graphic }),

  /**
   * A proof to keep: the marked PDF, made now and handed back as a link.
   *
   * Never the press file. What a customer downloads is stamped, so a design
   * nobody has paid for cannot be taken away and printed elsewhere.
   */
  proof: (uuid: string) => call<{ link: string }>('POST', `/designs/${uuid}/proof`),
};

/** A blank document for a product, matching Trimbox_Document::blank() in PHP. */
export function blankDocument(config: BootConfig): TrimboxDocument {
  return {
    schema: 1,
    product: { id: config.product.id, variation: config.product.variation },
    units: 'mm',
    bleed: config.size.bleed,
    safe: config.size.safe,
    pages: config.pages.map((page) => ({
      id: page.id,
      name: page.name,
      // A spread is two pages side by side, so it is twice as wide.
      size: page.size
        ? { w: page.size.w, h: page.size.h }
        : { w: page.spread ? config.size.w * 2 : config.size.w, h: config.size.h },
      background: null,
      layers: [],
      ...(page.spread ? { spread: true } : {}),
      ...(page.cut ? { cut: page.cut } : {}),
      ...(page.round ? { round: true } : {}),
      /*
       * What the product says about the page itself, rather than what is on it.
       * These come from the 3D setup and have to survive into the document, or
       * the designer shows a plain rectangle where a canvas wraps and a banner
       * hides half its height behind a cassette.
       */
      ...(page.wrap ? { wrap: page.wrap } : {}),
      ...(page.panels && page.panels > 1 ? { panels: page.panels } : {}),
      ...(page.edge ? { edge: page.edge } : {}),
      ...(page.shade && page.shade.length ? { shade: page.shade } : {}),
      ...(page.eyelets && page.eyelets.length ? { eyelets: page.eyelets } : {}),
    })),
    fonts: [],
    assets: [],
    ...cornersFor(config),
    ...bookletFor(config),
    ...(config.folds && (config.folds.across.length || config.folds.down.length)
      ? { folds: { across: config.folds.across, down: config.folds.down } }
      : {}),
  };
}

/**
 * The booklet a new design is, when the product is sold as one: its printed
 * page count, and which answer that came from where the shop prices it.
 */
export function bookletFor(config: BootConfig): { booklet?: TrimboxDocument['booklet'] } {
  if (!config.booklet || !config.booklet.pages) return {};

  const question = config.booklet.question;
  const answer = question
    ? question.answers.find((each) => each.key === question.chosen) ??
      question.answers.find((each) => each.key === question.fallback) ??
      question.answers[0]
    : undefined;

  return {
    booklet: {
      count: answer?.count || config.booklet.pages,
      ...(answer ? { choice: answer.key, label: answer.label } : {}),
    },
  };
}

/**
 * The corners a new card starts with: what was chosen on the product page, or
 * the question's own default. Nothing at all when the product does not ask.
 */
export function cornersFor(config: BootConfig): { corners?: TrimboxDocument['corners'] } {
  const question = config.corners;

  if (!question || question.answers.length === 0) return {};

  const answer =
    question.answers.find((each) => each.key === question.chosen) ??
    question.answers.find((each) => each.key === question.fallback) ??
    question.answers[0]!;

  return { corners: { radius: answer.radius, choice: answer.key, label: answer.label } };
}
